import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { controlToastState } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { DrugConfigOrg } from "../../../../../enums/drugConfigOrg";
import { RootState } from "../../../../../redux/store/store";
import { logError } from "../../../../../utils/customLogger";
import {
  DRUG_SPECIFIC_RULES_DOC_ID,
  GENERIC_RULES_DOC_ID,
  getDrugSpecificRulesCollectionPath,
  getDrugSpecificRulesDocumentPath,
  getGenericRulesCollectionPath,
  getGenericRulesDocumentPath,
  TOAST_IDS,
} from "../utils/constants";
import {
  ConfigurationType,
  MedicalNecessityRulesDocument,
  RuleType,
  SubRuleType,
} from "../utils/types";

const createEmptyDocument = (id: string): MedicalNecessityRulesDocument => ({
  id,
  evidence_based: { mcq: [], subjective: [] },
  fixed_response: { mcq: [], subjective: [] },
  policy_override: [],
});

const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

const normalize = (
  data: Partial<MedicalNecessityRulesDocument>,
): MedicalNecessityRulesDocument => ({
  id: data.id || "rules",
  evidence_based: {
    mcq: data.evidence_based?.mcq || [],
    subjective: data.evidence_based?.subjective || [],
  },
  fixed_response: {
    mcq: data.fixed_response?.mcq || [],
    subjective: data.fixed_response?.subjective || [],
  },
  policy_override: data.policy_override || [],
  updated_by: data.updated_by,
  updated_at: data.updated_at,
});

export const useMedicalNecessityRules = () => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  // ── User selections ──────────────────────────────────────────────────────
  const [organization, setOrganization] = useState<string>(DrugConfigOrg.NYCBS);
  const [configurationType, setConfigurationType] =
    useState<ConfigurationType>("generic");
  const [ruleType, setRuleType] = useState<RuleType>("evidence_based");
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);

  // ── Data ─────────────────────────────────────────────────────────────────
  const [originalRules, setOriginalRules] =
    useState<MedicalNecessityRulesDocument | null>(null);
  const [localRules, setLocalRules] =
    useState<MedicalNecessityRulesDocument | null>(null);

  // ── Async states ─────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch rules ──────────────────────────────────────────────────────────
  const fetchRules = useCallback(async () => {
    if (configurationType === "drug_specific" && !selectedDrugId) {
      setLocalRules(null);
      setOriginalRules(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const docPath =
        configurationType === "generic"
          ? getGenericRulesDocumentPath(organization)
          : getDrugSpecificRulesDocumentPath(organization, selectedDrugId!);

      const result =
        await FirestoreService.getDocument<
          Partial<MedicalNecessityRulesDocument>
        >(docPath);
      const normalized = normalize(result);
      setOriginalRules(deepClone(normalized));
      setLocalRules(deepClone(normalized));
    } catch (err) {
      logError(err as Error, "Error fetching medical necessity rules");

      // Drug-specific doc may not exist yet — initialize empty
      if (configurationType === "drug_specific") {
        const empty = createEmptyDocument(selectedDrugId || "rules");
        setOriginalRules(deepClone(empty));
        setLocalRules(deepClone(empty));
        setError(null);
      } else {
        setError((err as Error)?.message ?? "Failed to fetch rules");
      }
    } finally {
      setIsLoading(false);
    }
  }, [organization, configurationType, selectedDrugId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // ── Change detection ─────────────────────────────────────────────────────
  const hasChanges = useMemo(() => {
    if (!localRules || !originalRules) return false;
    return JSON.stringify(localRules) !== JSON.stringify(originalRules);
  }, [localRules, originalRules]);

  // ── Computed rule arrays for current ruleType ────────────────────────────
  const mcqRules = useMemo(() => {
    if (!localRules || ruleType === "policy_override") return [];
    return localRules[ruleType]?.mcq || [];
  }, [localRules, ruleType]);

  const subjectiveRules = useMemo(() => {
    if (!localRules || ruleType === "policy_override") return [];
    return localRules[ruleType]?.subjective || [];
  }, [localRules, ruleType]);

  const policyOverrideRules = useMemo(() => {
    if (!localRules || ruleType !== "policy_override") return [];
    return localRules.policy_override || [];
  }, [localRules, ruleType]);

  // ── Mutations (local only) ───────────────────────────────────────────────
  const addRule = useCallback(
    (subType: SubRuleType | "flat", ruleText: string) => {
      setLocalRules((prev) => {
        if (!prev) return prev;
        const next = deepClone(prev);
        if (ruleType === "policy_override" || subType === "flat") {
          next.policy_override.push(ruleText);
        } else {
          next[ruleType][subType].push(ruleText);
        }
        return next;
      });
    },
    [ruleType],
  );

  const editRule = useCallback(
    (subType: SubRuleType | "flat", index: number, newText: string) => {
      setLocalRules((prev) => {
        if (!prev) return prev;
        const next = deepClone(prev);
        if (ruleType === "policy_override" || subType === "flat") {
          next.policy_override[index] = newText;
        } else {
          next[ruleType][subType][index] = newText;
        }
        return next;
      });
    },
    [ruleType],
  );

  const deleteRule = useCallback(
    (subType: SubRuleType | "flat", index: number) => {
      setLocalRules((prev) => {
        if (!prev) return prev;
        const next = deepClone(prev);
        if (ruleType === "policy_override" || subType === "flat") {
          next.policy_override.splice(index, 1);
        } else {
          next[ruleType][subType].splice(index, 1);
        }
        return next;
      });
    },
    [ruleType],
  );

  // ── Save to Firestore ────────────────────────────────────────────────────
  const saveChanges = useCallback(async () => {
    if (!localRules) return;
    setIsSaving(true);

    try {
      const dataToSave = {
        evidence_based: localRules.evidence_based,
        fixed_response: localRules.fixed_response,
        policy_override: localRules.policy_override,
        updated_by: user?.email || "N/A",
        updated_at: moment().toDate().toISOString(),
      };

      if (configurationType === "generic") {
        await FirestoreService.updateDocument(
          getGenericRulesCollectionPath(organization),
          GENERIC_RULES_DOC_ID,
          dataToSave,
        );
      } else {
        // Use setDocument for drug-specific (handles first-time creation)
        await FirestoreService.setDocument(
          getDrugSpecificRulesCollectionPath(organization, selectedDrugId!),
          DRUG_SPECIFIC_RULES_DOC_ID,
          dataToSave,
        );
      }

      controlToastState(TOAST_IDS.SAVE_SUCCESS);
      await fetchRules();
    } catch (err) {
      logError(err as Error, "Error saving medical necessity rules");
      controlToastState(TOAST_IDS.SAVE_FAILURE);
    } finally {
      setIsSaving(false);
    }
  }, [
    localRules,
    configurationType,
    organization,
    selectedDrugId,
    user?.email,
    fetchRules,
  ]);

  return {
    // Data
    rules: localRules,
    isLoading,
    isSaving,
    error,
    hasChanges,

    // Selections
    organization,
    configurationType,
    ruleType,
    selectedDrugId,

    // Computed arrays
    mcqRules,
    subjectiveRules,
    policyOverrideRules,

    // Setters
    setOrganization,
    setConfigurationType,
    setRuleType,
    setSelectedDrugId,

    // CRUD
    addRule,
    editRule,
    deleteRule,
    saveChanges,
    refreshRules: fetchRules,

    // Metadata
    updatedBy: localRules?.updated_by || null,
    updatedAt: localRules?.updated_at || null,
  };
};
